import { useEffect, useMemo, useState } from "react";

import { Avatar } from "./ui/Avatar";
import { cn } from "./ui/cn";
import type { User } from "../types";

/**
 * Handing a ticket to somebody, sized for a thumb.
 *
 * The desktop does this with a native `<select>` in the details column, which
 * is the right control under a pointer and the wrong one on a phone: the
 * column is a sheet two taps away, and once there the select opens a wheel of
 * bare names with no way to type one. Reassigning was the job most often
 * reported as hunting for something on a handset, so it gets a picker of its
 * own rather than a restyled dropdown.
 *
 * A bottom sheet on a phone and a small centred card from `md` up, so a caller
 * on a wide screen gets something sensible without having to choose. Rows are
 * 48px, comfortably over the 44px a finger needs, and "Me" leads the list
 * because picking a ticket up is far and away the commonest reassignment.
 *
 * The search box is not focused on open. On a phone that raises the keyboard
 * over the very list the agent was about to tap, and a team is short enough
 * that most picks need no typing at all.
 */
export function AssigneeSheet({
  users,
  currentUserId,
  assigneeId,
  subject,
  onPick,
  onClose,
}: {
  users: User[];
  currentUserId: number;
  /** Who has it now, ticked in the list; null for unassigned. */
  assigneeId: number | null;
  /** The ticket's subject, so a sheet opened from the queue says which one. */
  subject?: string;
  onPick: (assigneeId: number | null) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /*
   * The signed-in agent first, then "Unassigned", then everybody else by
   * name. Searching narrows all three, and drops "Unassigned" unless the
   * words could mean it -- a search for "sam" that still offered to unassign
   * would put the wrong row under the thumb.
   */
  const needle = query.trim().toLowerCase();
  const { me, people } = useMemo(() => {
    const matches = (user: User): boolean =>
      !needle ||
      user.name.toLowerCase().includes(needle) ||
      user.email.toLowerCase().includes(needle);

    const self = users.find((user) => user.id === currentUserId);
    return {
      me: self && matches(self) ? self : null,
      people: users
        .filter((user) => user.id !== currentUserId && matches(user))
        .sort((a, b) => a.name.localeCompare(b.name, "en-GB")),
    };
  }, [users, currentUserId, needle]);

  const showUnassigned = !needle || "unassigned".includes(needle);

  const pick = (id: number | null): void => {
    /* Closed before the request rather than after it: the caller shows the
       new name straight away, and a sheet left open over a pending save reads
       as a tap that did not register. */
    onClose();
    if (id !== assigneeId) onPick(id);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 md:items-start md:p-6 md:pt-24"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Assign ticket"
        className={cn(
          "flex w-full flex-col overflow-hidden border-line bg-surface shadow-xl",
          /* `dvh`, as Overlay explains: on iOS `vh` is the viewport with the
             toolbars hidden, and the bottom rows would sit under Safari's. */
          "max-h-[85dvh] rounded-t-2xl border-t",
          "md:max-h-[70dvh] md:max-w-sm md:rounded-lg md:border",
        )}
      >
        <header className="shrink-0 border-b border-line px-4 pb-3 pt-2">
          {/* The grabber says "sheet" at a glance; it is not draggable. */}
          <div
            aria-hidden
            className="mx-auto mb-2 h-1 w-10 rounded-full bg-line md:hidden"
          />
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-content">Assign to</h2>
              {subject ? (
                <p className="truncate text-xs text-muted">{subject}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex size-11 shrink-0 items-center justify-center rounded-md text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400"
            >
              ✕
            </button>
          </div>

          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search people"
            aria-label="Search people"
            className="mt-2 h-11 w-full rounded-lg border border-line bg-surface-2 px-3 text-sm text-content outline-none focus:border-brand-500 md:h-9"
          />
        </header>

        <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1 pb-[max(0.25rem,env(safe-area-inset-bottom))]">
          {me ? (
            <Row
              selected={assigneeId === me.id}
              onClick={() => pick(me.id)}
              avatar={
                <Avatar
                  size="md"
                  agent={{ id: me.id, name: me.name, color: me.avatarColor }}
                />
              }
              label={`${me.name} (me)`}
            />
          ) : null}

          {showUnassigned ? (
            <Row
              selected={assigneeId === null}
              onClick={() => pick(null)}
              avatar={<Avatar size="md" agent={null} />}
              label="Unassigned"
              muted
            />
          ) : null}

          {people.map((user) => (
            <Row
              key={user.id}
              selected={assigneeId === user.id}
              onClick={() => pick(user.id)}
              avatar={
                <Avatar
                  size="md"
                  agent={{ id: user.id, name: user.name, color: user.avatarColor }}
                />
              }
              label={user.name}
            />
          ))}

          {!me && !showUnassigned && people.length === 0 ? (
            <li className="px-4 py-6 text-center text-sm text-muted">
              Nobody by that name.
            </li>
          ) : null}
        </ul>
      </div>
    </div>
  );
}

function Row({
  selected,
  onClick,
  avatar,
  label,
  muted = false,
}: {
  selected: boolean;
  onClick: () => void;
  avatar: React.ReactNode;
  label: string;
  muted?: boolean;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-current={selected ? "true" : undefined}
        className={cn(
          "flex min-h-12 w-full items-center gap-3 px-4 py-2 text-left text-[15px]",
          "hover:bg-surface-2 active:bg-surface-2 focus:outline-none focus-visible:bg-surface-2",
          selected && "bg-brand-50 dark:bg-brand-950/40",
          muted ? "text-muted" : "text-content",
        )}
      >
        {avatar}
        <span className={cn("min-w-0 flex-1 truncate", selected && "font-semibold")}>
          {label}
        </span>
        {selected ? (
          <span aria-hidden className="text-brand-600 dark:text-brand-400">
            ✓
          </span>
        ) : null}
      </button>
    </li>
  );
}
